import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Play, FileText, Plus, Trash2, Check, Pencil, X, Upload, GraduationCap } from 'lucide-react';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { coursesApi, mediaApi } from '../api';
import { useAuth } from '../context/AuthContext';

const LESSON_TYPES = [
  { value: 'video', label: 'Video' },
  { value: 'text', label: 'Tekst' },
  { value: 'quiz', label: 'Quiz' },
  { value: 'assignment', label: 'Zadanie' },
];

/** Formularz lekcji — instruktor dodaje/edytuje lekcję. */
function LessonModal({ courseId, lesson, nextOrder, onClose, onSaved }) {
  const editing = !!lesson;
  const [form, setForm] = useState({
    title: lesson?.title || '',
    description: lesson?.description || '',
    type: lesson?.type || 'video',
    content_url: lesson?.content_url || '',
    duration_minutes: lesson?.duration_minutes || 0,
    order: lesson?.order ?? nextOrder,
    drip_days: lesson?.drip_days || 0,
    is_free_preview: lesson?.is_free_preview || false,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const r = await mediaApi.upload(file);
      setForm(f => ({ ...f, content_url: r.data?.url || '' }));
    } catch (err) {
      alert('Upload: ' + (err?.response?.data?.detail || err.message));
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || saving) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        duration_minutes: Number(form.duration_minutes) || 0,
        order: Number(form.order) || 0,
        drip_days: Number(form.drip_days) || 0,
      };
      if (editing) await coursesApi.updateLesson(courseId, lesson.id, payload);
      else await coursesApi.createLesson(courseId, payload);
      onSaved();
    } catch (err) {
      alert('Lekcja: ' + (err?.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl max-w-lg w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold">{editing ? 'Edytuj lekcję' : 'Nowa lekcja'}</h2>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Tytuł *</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required
                   className="w-full px-3 py-2 border border-slate-200 rounded text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Opis</label>
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                   className="w-full px-3 py-2 border border-slate-200 rounded text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Typ</label>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded text-sm bg-white">
                {LESSON_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Czas (min)</label>
              <input type="number" min="0" value={form.duration_minutes}
                     onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))}
                     className="w-full px-3 py-2 border border-slate-200 rounded text-sm" />
            </div>
          </div>
          {form.type === 'text' ? (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Treść lekcji</label>
              <textarea value={form.content_url} onChange={e => setForm(f => ({ ...f, content_url: e.target.value }))}
                        rows={5} className="w-full px-3 py-2 border border-slate-200 rounded text-sm resize-none" />
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                {form.type === 'video' ? 'Video — URL lub upload' : 'Materiał — URL'}
              </label>
              <div className="flex items-center gap-2">
                <input value={form.content_url} onChange={e => setForm(f => ({ ...f, content_url: e.target.value }))}
                       placeholder="https://… (mp4, YouTube, plik)"
                       className="flex-1 px-3 py-2 border border-slate-200 rounded text-sm" />
                <label className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-2 text-xs border border-slate-300 rounded cursor-pointer hover:bg-slate-50 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                  <Upload className="w-3.5 h-3.5" /> {uploading ? '…' : 'Wgraj'}
                  <input type="file" className="hidden" onChange={upload} />
                </label>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Kolejność</label>
              <input type="number" min="0" value={form.order}
                     onChange={e => setForm(f => ({ ...f, order: e.target.value }))}
                     className="w-full px-3 py-2 border border-slate-200 rounded text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Drip (dni od zapisu)</label>
              <input type="number" min="0" value={form.drip_days}
                     onChange={e => setForm(f => ({ ...f, drip_days: e.target.value }))}
                     className="w-full px-3 py-2 border border-slate-200 rounded text-sm" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.is_free_preview}
                   onChange={e => setForm(f => ({ ...f, is_free_preview: e.target.checked }))} className="w-4 h-4" />
            Darmowy podgląd
          </label>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-slate-200">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded">Anuluj</button>
          <button type="submit" disabled={!form.title.trim() || saving}
                  className="flex items-center gap-1 px-4 py-2 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? 'Zapisz' : 'Dodaj lekcję'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function CourseDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState({ enrolled: false, progress_percent: 0, completed_lesson_ids: [] });
  const [loading, setLoading] = useState(true);
  const [activeLesson, setActiveLesson] = useState(null);
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [editLesson, setEditLesson] = useState(null);
  const [busy, setBusy] = useState(false);

  const isInstructor = course && user && course.instructor_id === user.id;

  const load = async () => {
    try {
      const [c, l, p] = await Promise.all([
        coursesApi.get(id),
        coursesApi.listLessons(id),
        coursesApi.getProgress(id).catch(() => ({ data: { enrolled: false, progress_percent: 0, completed_lesson_ids: [] } })),
      ]);
      setCourse(c.data);
      setLessons(l.data?.items || []);
      setProgress(p.data || { enrolled: false, progress_percent: 0, completed_lesson_ids: [] });
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const enroll = async () => {
    setBusy(true);
    try { await coursesApi.enroll(id); await load(); }
    catch (e) { alert(e?.response?.data?.detail || 'Nie udało się zapisać'); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try { await coursesApi.update(id, { status: 'published' }); await load(); }
    catch (e) { alert(e?.response?.data?.detail || 'Błąd publikacji'); }
    finally { setBusy(false); }
  };

  const removeLesson = async (lid) => {
    if (!window.confirm('Usunąć lekcję?')) return;
    try {
      await coursesApi.deleteLesson(id, lid);
      if (activeLesson?.id === lid) setActiveLesson(null);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Błąd'); }
  };

  const markComplete = async (lid) => {
    try {
      const r = await coursesApi.completeLesson(id, lid);
      await load();
      if (r.data?.course_completed) alert('Gratulacje — kurs ukończony!');
    } catch (e) { alert(e?.response?.data?.detail || 'Błąd'); }
  };

  if (loading) {
    return <DashboardLayout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div></DashboardLayout>;
  }
  if (!course) {
    return <DashboardLayout><p className="text-slate-500">Kurs nie znaleziony.</p></DashboardLayout>;
  }

  const completedIds = new Set(progress.completed_lesson_ids || []);

  return (
    <DashboardLayout>
      <Link to="/dashboard/courses" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-4">
        <ArrowLeft className="w-4 h-4" /> Kursy
      </Link>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
        {course.image && <img src={course.image} alt="" className="w-full h-40 object-cover" />}
        <div className="p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold text-slate-900">{course.title}</h1>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${course.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {course.status === 'published' ? 'Opublikowany' : 'Szkic'}
                </span>
              </div>
              <p className="text-slate-600 mt-1">{course.description}</p>
              <div className="flex gap-4 mt-2 text-sm text-slate-500 flex-wrap">
                <span className="flex items-center gap-1"><GraduationCap className="w-4 h-4" /> {course.lessons_count || 0} lekcji</span>
                <span>{course.total_duration_minutes || 0} min</span>
                <span>{course.students_count || 0} kursantów</span>
              </div>
            </div>
            {isInstructor && course.status !== 'published' && (
              <button onClick={publish} disabled={busy}
                      className="shrink-0 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
                Publikuj kurs
              </button>
            )}
          </div>

          {!isInstructor && progress.enrolled && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                <span>Postęp</span><span>{progress.progress_percent}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-indigo-500" style={{ width: `${progress.progress_percent}%` }} />
              </div>
            </div>
          )}
          {!isInstructor && !progress.enrolled && (
            <button onClick={enroll} disabled={busy}
                    className="mt-4 px-4 py-2 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
              Zapisz się na kurs
            </button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        {/* Lista lekcji */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 h-fit">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-900">Lekcje</h2>
            {isInstructor && (
              <button onClick={() => { setEditLesson(null); setShowLessonModal(true); }}
                      className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700">
                <Plus className="w-3.5 h-3.5" /> Dodaj
              </button>
            )}
          </div>
          {lessons.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">Brak lekcji.</p>
          ) : (
            <div className="space-y-1">
              {lessons.map((l, i) => {
                const done = completedIds.has(l.id);
                return (
                  <div key={l.id} className={`flex items-center gap-2 p-2 rounded-lg ${activeLesson?.id === l.id ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}>
                    <button onClick={() => setActiveLesson(l)} className="flex-1 flex items-center gap-2 text-left min-w-0">
                      <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                        {done
                          ? <Check className="w-3.5 h-3.5 text-emerald-600" />
                          : (l.type === 'text' ? <FileText className="w-3.5 h-3.5 text-slate-400" /> : <Play className="w-3.5 h-3.5 text-slate-400" />)}
                      </span>
                      <span className="text-sm text-slate-700 truncate">{i + 1}. {l.title}</span>
                      {l.drip_days > 0 && <span className="text-[10px] text-amber-600 shrink-0">drip {l.drip_days}d</span>}
                    </button>
                    {isInstructor && (
                      <span className="flex items-center gap-1 shrink-0">
                        <button onClick={() => { setEditLesson(l); setShowLessonModal(true); }} className="text-slate-400 hover:text-indigo-600"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => removeLesson(l.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Viewer lekcji */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          {!activeLesson ? (
            <p className="text-sm text-slate-400 text-center py-16">Wybierz lekcję z listy.</p>
          ) : (
            <>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">{activeLesson.title}</h3>
              {activeLesson.description && <p className="text-sm text-slate-500 mb-3">{activeLesson.description}</p>}
              {activeLesson.type === 'text' ? (
                <div className="text-slate-800 whitespace-pre-wrap text-sm">{activeLesson.content_url || '(brak treści)'}</div>
              ) : activeLesson.content_url ? (
                /youtube\.com|youtu\.be|vimeo\.com/.test(activeLesson.content_url) ? (
                  <iframe
                    src={activeLesson.content_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                    className="w-full aspect-video rounded-lg border border-slate-200"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen title="lekcja"
                  />
                ) : (
                  <video controls src={activeLesson.content_url} className="w-full max-h-[420px] rounded-lg border border-slate-200" />
                )
              ) : (
                <p className="text-sm text-slate-400">(brak materiału)</p>
              )}

              {!isInstructor && progress.enrolled && (
                <button
                  onClick={() => markComplete(activeLesson.id)}
                  disabled={completedIds.has(activeLesson.id)}
                  className="mt-4 inline-flex items-center gap-1 px-4 py-2 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  <Check className="w-4 h-4" /> {completedIds.has(activeLesson.id) ? 'Ukończona' : 'Oznacz jako ukończoną'}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {showLessonModal && (
        <LessonModal
          courseId={id}
          lesson={editLesson}
          nextOrder={lessons.length}
          onClose={() => setShowLessonModal(false)}
          onSaved={() => { setShowLessonModal(false); load(); }}
        />
      )}
    </DashboardLayout>
  );
}
